import { useMutation, useQuery } from '@tanstack/react-query'
import { ProductPictureImportsFilterParams, CreateProductPictureImportParams } from '@/types/product-picture-import'
import { useProductPictureImportsStore } from '@/store'
import { useBulkDeleteSuccessHandler } from './use-bulk-delete'

export const useProductPictureImports = (params: ProductPictureImportsFilterParams) => {
  const fetchProductPictureImports = useProductPictureImportsStore(state => state.fetchProductPictureImports)

  return useQuery({
    queryKey: ['productPictureImports', params],
    queryFn: () => fetchProductPictureImports(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  })
}

export const useProductPictureImport = (id: string) => {
  const getProductPictureImport = useProductPictureImportsStore(state => state.getProductPictureImport)

  return useQuery({
    queryKey: ['productPictureImport', id],
    queryFn: () => getProductPictureImport(id),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!id
  })
}

export const useCreateProductPictureImport = () => {
  const createProductPictureImport = useProductPictureImportsStore(state => state.createProductPictureImport)

  return useMutation({
    mutationFn: (params: CreateProductPictureImportParams) => createProductPictureImport(params),
    onSuccess: () => {}
  })
}

export const useDeleteProductPictureImport = () => {
  const deleteProductPictureImport = useProductPictureImportsStore(state => state.deleteProductPictureImport)

  return useMutation({
    mutationFn: (id: string) => deleteProductPictureImport(id),
    onSuccess: () => {}
  })
}

export const useRetryProductPictureImport = () => {
  const retryProductPictureImport = useProductPictureImportsStore(state => state.retryProductPictureImport)

  return useMutation({
    mutationFn: (id: string) => retryProductPictureImport(id),
    onSuccess: () => {}
  })
}

export const useBulkDeleteProductPictureImports = () => {
  const bulkDeleteProductPictureImports = useProductPictureImportsStore(state => state.bulkDeleteProductPictureImports)
  const deselectAllProductPictureImports = useProductPictureImportsStore(
    state => state.deselectAllProductPictureImports
  )
  const handleBulkDeleteSuccess = useBulkDeleteSuccessHandler()

  return useMutation({
    mutationFn: (ids: string[]) => bulkDeleteProductPictureImports(ids),
    onSuccess: response => {
      deselectAllProductPictureImports()
      handleBulkDeleteSuccess(response)
    }
  })
}
